// Catalyst Events section for the company profile.
//
// Fetches events live from the catalyst_event search endpoint scoped to this
// company (company_id reference match) instead of reading a materialized field.
// This keeps the profile always-current in production with no backfill, and
// supports the full event history paginated + grouped into collapsible
// month/year sections so the profile stays uncluttered.

import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link as JoyLink,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import EventIcon from "@mui/icons-material/Event";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useBKGCatalystEventSearch } from "../hooks";
import { stripSourceRefs } from "../../../utils/stripSourceRefs";
import { groupCatalystEventsByMonth } from "../utils/groupCatalystEventsByMonth";
import { compactSummary } from "../utils/compactSummary";

const PAGE_SIZE = 50;

/**
 * Extract a usable source URL for an event. The link lives on the event's
 * `source_id` (and occasionally the per-field source_id); only http(s) values
 * are real links — other ingestion sources (e.g. "manual") are ignored.
 */
function getEventUrl(event: any): string | null {
  const candidates = [
    event?.source_id,
    event?.value?.summary?.source_id,
    event?.value?.title?.source_id,
  ];
  for (const candidate of candidates) {
    if (
      typeof candidate === "string" &&
      /^https?:\/\//i.test(candidate.trim())
    ) {
      return candidate.trim();
    }
  }
  return null;
}

/** Short, human label for a source link (hostname without the leading www.). */
function getSourceLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "Source";
  }
}

// Uniform accent/chip color for every event card. Kept as a single constant so
// the styling stays consistent and is trivial to retheme in one place.
const EVENT_ACCENT_COLOR = "primary" as const;

interface CompanyCatalystEventsProps {
  workspaceId: string;
  companyId?: string;
}

const CompanyCatalystEvents: React.FC<CompanyCatalystEventsProps> = ({
  workspaceId,
  companyId,
}) => {
  const search = useBKGCatalystEventSearch();
  const [events, setEvents] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const fetchPage = useCallback(
    (skip: number) =>
      search(workspaceId, {
        skip,
        limit: PAGE_SIZE,
        company_id: companyId,
      }),
    [search, workspaceId, companyId],
  );

  useEffect(() => {
    if (!workspaceId || !companyId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPage(0)
      .then((res) => {
        if (cancelled) return;
        setEvents(res.results || []);
        setTotal(res.total || 0);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("Failed to load company catalyst events:", e);
        setError("Failed to load catalyst events.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, companyId, fetchPage]);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await fetchPage(events.length);
      setEvents((prev) => [...prev, ...(res.results || [])]);
      setTotal(res.total || 0);
    } catch (e) {
      console.error("Failed to load more catalyst events:", e);
      setError("Failed to load more catalyst events.");
    } finally {
      setLoadingMore(false);
    }
  };

  // Nothing to show: hide the whole card (mirrors the News section behavior).
  if (!loading && !error && events.length === 0) return null;

  const groups = groupCatalystEventsByMonth(events);

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <EventIcon color="primary" />
          <Typography level="h3">Catalyst Events</Typography>
          {total > 0 && (
            <Chip size="sm" variant="soft" color="neutral">
              {total}
            </Chip>
          )}
        </Stack>

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size="sm" />
            <Typography level="body-sm">Loading catalyst events…</Typography>
          </Stack>
        ) : error ? (
          <Typography level="body-sm" color="danger">
            {error}
          </Typography>
        ) : (
          <>
            <AccordionGroup disableDivider>
              {groups.map((group) => {
                // All groups start collapsed; user opens the months they want.
                const isExpanded = expanded[group.key] ?? false;
                return (
                  <Accordion
                    key={group.key}
                    expanded={isExpanded}
                    onChange={(_e, val) =>
                      setExpanded((prev) => ({ ...prev, [group.key]: val }))
                    }
                  >
                    <AccordionSummary>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ width: "100%" }}
                      >
                        <Typography level="title-sm">{group.label}</Typography>
                        <Chip size="sm" variant="soft" color="primary">
                          {group.events.length}
                        </Chip>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={1.5} sx={{ pt: 1 }}>
                        {group.events.map((event: any, index: number) => {
                          const title =
                            event?.value?.title?.value ?? event?.value?.title;
                          const type =
                            event?.value?.type?.value ?? event?.value?.type;
                          const date =
                            event?.value?.date?.value ?? event?.value?.date;
                          const summary =
                            event?.value?.summary?.value ??
                            event?.value?.summary;
                          const url = getEventUrl(event);
                          return (
                            <Sheet
                              key={event?._id ?? index}
                              variant="soft"
                              color="neutral"
                              sx={{
                                borderRadius: "md",
                                p: 1.75,
                                borderLeft: "3px solid",
                                borderLeftColor: `${EVENT_ACCENT_COLOR}.400`,
                                transition: "box-shadow 0.2s ease",
                                "&:hover": { boxShadow: "sm" },
                              }}
                            >
                              <Stack spacing={1}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="flex-start"
                                  justifyContent="space-between"
                                >
                                  <Typography
                                    level="title-sm"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {title || "Catalyst Event"}
                                  </Typography>
                                  {date && (
                                    <Typography
                                      level="body-xs"
                                      textColor="text.tertiary"
                                      sx={{ whiteSpace: "nowrap", pt: 0.25 }}
                                    >
                                      {date}
                                    </Typography>
                                  )}
                                </Stack>

                                {type && (
                                  <Box>
                                    <Chip
                                      size="sm"
                                      variant="soft"
                                      color={EVENT_ACCENT_COLOR}
                                    >
                                      {type}
                                    </Chip>
                                  </Box>
                                )}

                                {summary && (
                                  <Typography
                                    level="body-sm"
                                    textColor="text.secondary"
                                  >
                                    {compactSummary(stripSourceRefs(summary))}
                                  </Typography>
                                )}

                                {url && (
                                  <>
                                    <Divider />
                                    <JoyLink
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      level="body-xs"
                                      startDecorator={
                                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                                      }
                                      sx={{
                                        width: "fit-content",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {getSourceLabel(url)}
                                    </JoyLink>
                                  </>
                                )}
                              </Stack>
                            </Sheet>
                          );
                        })}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </AccordionGroup>

            {events.length < total && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  size="sm"
                  onClick={handleLoadMore}
                  loading={loadingMore}
                >
                  Load more ({total - events.length} older)
                </Button>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CompanyCatalystEvents;
